import { apiClient } from './api'

export type ACLRole = 'viewer' | 'contributor' | 'manager' | 'owner'
export type PrincipalType = 'user' | 'group'
export type ACLEntityType = 'portfolio' | 'program' | 'project' | 'task'

export interface OrgGroup {
  id: number
  organization_id?: number | null
  name: string
  description?: string | null
  kind: string
  member_count: number
  created_at: string
}

export interface OrgGroupMember {
  user_id: number
  added_at: string
  full_name?: string | null
  email?: string | null
}

export interface ACLGrant {
  id: number
  organization_id?: number | null
  principal_type: PrincipalType
  principal_id: number
  principal_label?: string | null
  entity_type: ACLEntityType
  entity_id: number
  role: ACLRole
  granted_by?: number | null
  granted_at: string
}

export interface FieldVisibility {
  id: number
  organization_id?: number | null
  entity_type: ACLEntityType
  field_key: string
  field_source: 'builtin' | 'custom'
  min_role: ACLRole
  created_at: string
}

export const groupsService = {
  list: async (organizationId?: number): Promise<OrgGroup[]> => {
    const { data } = await apiClient.get('/api/v1/groups/', {
      params: organizationId ? { organization_id: organizationId } : undefined,
    })
    return data
  },
  create: async (input: {
    name: string
    description?: string
    kind?: string
    organization_id?: number
  }): Promise<OrgGroup> => {
    const { data } = await apiClient.post('/api/v1/groups/', input)
    return data
  },
  update: async (
    id: number,
    input: { name?: string; description?: string; kind?: string },
  ): Promise<OrgGroup> => {
    const { data } = await apiClient.patch(`/api/v1/groups/${id}`, input)
    return data
  },
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/v1/groups/${id}`)
  },
  members: async (id: number): Promise<OrgGroupMember[]> => {
    const { data } = await apiClient.get(`/api/v1/groups/${id}/members`)
    return data
  },
  replaceMembers: async (id: number, userIds: number[]): Promise<OrgGroupMember[]> => {
    const { data } = await apiClient.put(`/api/v1/groups/${id}/members`, {
      user_ids: userIds,
    })
    return data
  },
}

export const aclService = {
  list: async (params: {
    entity_type?: ACLEntityType
    entity_id?: number
    principal_type?: PrincipalType
    principal_id?: number
    organization_id?: number
  }): Promise<ACLGrant[]> => {
    const { data } = await apiClient.get('/api/v1/acl/', { params })
    return data
  },
  upsert: async (input: {
    principal_type: PrincipalType
    principal_id: number
    entity_type: ACLEntityType
    entity_id: number
    role: ACLRole
  }): Promise<ACLGrant> => {
    const { data } = await apiClient.post('/api/v1/acl/', input)
    return data
  },
  revoke: async (grantId: number): Promise<void> => {
    await apiClient.delete(`/api/v1/acl/${grantId}`)
  },
  myEffectiveRole: async (
    entityType: ACLEntityType,
    entityId: number,
  ): Promise<{ role: ACLRole | null }> => {
    const { data } = await apiClient.get('/api/v1/acl/effective', {
      params: { entity_type: entityType, entity_id: entityId },
    })
    return data
  },
}

export const fieldVisibilityService = {
  list: async (
    entityType?: ACLEntityType,
    organizationId?: number,
  ): Promise<FieldVisibility[]> => {
    const { data } = await apiClient.get('/api/v1/field-visibility/', {
      params: {
        ...(entityType ? { entity_type: entityType } : {}),
        ...(organizationId ? { organization_id: organizationId } : {}),
      },
    })
    return data
  },
  defaults: async (): Promise<
    Array<{ entity_type: string; field_key: string; min_role: ACLRole; field_source: string }>
  > => {
    const { data } = await apiClient.get('/api/v1/field-visibility/defaults')
    return data
  },
  upsert: async (
    input: {
      entity_type: ACLEntityType
      field_key: string
      field_source?: 'builtin' | 'custom'
      min_role: ACLRole
    },
    organizationId?: number,
  ): Promise<FieldVisibility> => {
    const { data } = await apiClient.post('/api/v1/field-visibility/', input, {
      params: organizationId ? { organization_id: organizationId } : undefined,
    })
    return data
  },
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/v1/field-visibility/${id}`)
  },
}
